import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useFeatureFlag } from "src/utils/hooks/useFeatureFlag";
import { useTrEnabled } from "src/utils/hooks/useTrEnabled";
import TrThirdPartyBlockModal from "src/components/transfers/TrThirdPartyBlockModal";

export interface AddressTrOwnershipValue {
  isFirstParty: boolean | null;
  thirdPartyRecipientName?: string;
}

export interface AddressTrOwnershipProps {
  value: AddressTrOwnershipValue;
  onChange: (next: AddressTrOwnershipValue) => void;
  /**
   * Called when the third-party block triggers. Parents (D3 + D4) use this
   * to disable Save and to optionally surface their own modal frame.
   */
  onBlocked?: (blocked: boolean) => void;
  /**
   * If true, the block-modal is rendered inline. D3 (new-address flow)
   * renders inline; D4 (`CryptoWithdrawalTrBackfill`) renders its own.
   */
  renderBlockModal?: boolean;
  /**
   * D3 cancel handler. Required when `renderBlockModal` is true (D3 flow)
   * — without it the full-screen block dialog cannot be dismissed (the
   * modal's default cancel resets the *withdrawal* slice, which has no
   * effect on the address-testing slice). Should close the parent
   * address-testing modal entirely.
   */
  onCancelAddressFlow?: () => void;
  /** Optional title — defaults to "Is this your own account?" */
  title?: string;
}

/**
 * D3 step 1 — "Is this your own account?" radio.
 *
 * On "No", reveal the recipient-name field. If the entity has the
 * `TR_THIRD_PARTY_BLOCK` flag enabled, render the inline block modal and
 * inform the parent via `onBlocked` so it can disable Save. The actual
 * server-side enforcement happens in `withdrawal.service.addUserWithdrawAddress`
 * — this component is purely informational/UX.
 */
const AddressTrOwnership: React.FC<AddressTrOwnershipProps> = ({
  value,
  onChange,
  onBlocked,
  renderBlockModal = true,
  onCancelAddressFlow,
  title = "Is this your own account?",
}) => {
  const { enabled: blockFlagEnabled } = useFeatureFlag("TR_THIRD_PARTY_BLOCK");
  // Master TR kill-switch — when outbound TR is off globally, the
  // third-party block is a no-op (backend skips enforcement too). Prevents
  // a half-flipped state where the modal blocks UX but the API would
  // accept the third-party address anyway.
  const trOn = useTrEnabled("OUTBOUND");
  const isThirdParty = value.isFirstParty === false;
  const blocked = isThirdParty && blockFlagEnabled && trOn;

  React.useEffect(() => {
    onBlocked?.(blocked);
  }, [blocked, onBlocked]);

  // Defensive self-guard. Visibility is already gated by the parent step
  // machine (AddressTesting only advances to the TR steps when `trOn` is
  // true), but this component must never render the TR ownership question
  // while outbound TR is off — a mis-set step (restored state, deep-link, or
  // future code) must not expose a Travel-Rule surface for a dark feature.
  // Kept after the hooks above so the rules of hooks hold on every render.
  if (!trOn) return null;

  const handleRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value === "yes";
    onChange({
      isFirstParty: next,
      // Clear the recipient name if we flipped back to first-party.
      thirdPartyRecipientName: next ? undefined : value.thirdPartyRecipientName,
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      thirdPartyRecipientName: e.target.value,
    });
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }} data-testid="address-tr-ownership-title">
        {title}
      </Typography>
      <FormControl>
        <RadioGroup
          value={value.isFirstParty === null ? "" : value.isFirstParty ? "yes" : "no"}
          onChange={handleRadio}>
          <FormControlLabel
            value="yes"
            control={<Radio />}
            label="Yes — this is my own wallet or account"
            data-testid="address-tr-ownership-yes"
          />
          <FormControlLabel
            value="no"
            control={<Radio />}
            label="No — this belongs to someone else"
            data-testid="address-tr-ownership-no"
          />
        </RadioGroup>
      </FormControl>
      {isThirdParty && !blocked && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Recipient full legal name"
            value={value.thirdPartyRecipientName || ""}
            onChange={handleNameChange}
            inputProps={{ "data-testid": "address-tr-recipient-name" }}
            required
          />
        </Box>
      )}
      {renderBlockModal && (
        <TrThirdPartyBlockModal
          open={blocked}
          scope="thirdPartyBlock"
          // "Use a different address" inside the address-testing flow means
          // "let me change my answer" — clearing isFirstParty makes the
          // block modal close (blocked → false) and re-enables the radio.
          onUseDifferentAddress={() =>
            onChange({ isFirstParty: null, thirdPartyRecipientName: undefined })
          }
          // Cancel exits the address-add flow entirely. Parent should wire
          // onCancelAddressFlow; if it doesn't, we still clear isFirstParty
          // as a defensive fallback so the modal at least closes (otherwise
          // the modal's default cancel hits the unrelated withdrawal slice
          // and the user is trapped in a full-screen overlay).
          onCancel={
            onCancelAddressFlow ??
            (() => onChange({ isFirstParty: null, thirdPartyRecipientName: undefined }))
          }
        />
      )}
    </Box>
  );
};

export default AddressTrOwnership;
